import React, { FC, useEffect, useState } from "react";
import LmsTable from "../ui/LmsTable";
import { useFetchData, useParseRouteActions } from "../../../helpers/GlobalHelper";
import LmsFormGenerator, { BCFormController } from "../ui/LmsFormGenerator";
import { purchaseAdviceQuoteViewFields } from "../ui/LmsSharedForms";
import { converToStartDateToEndDateString } from "../../../Helpers";

interface LoanSaleApprovalsEditFormProps {
  id: string;
}

const LmsServicingLoanSaleApprovalsMainPage: FC = () => {
  return (
    <LmsTable
      apiSuffix="purchasequotes/loan-sale-approvals" 
      actionButtons={["edit", "export"]}
      columnLabelsDictionary={{
        saleTypeOption: "Sale Type",
        statusOption: "Status",
        recognitionOption: "Revenue Recognition Method"
      }}
      columnClickOptions={{
        loan: {
          path: "servicing-loan-sale-approvals/edit",
          idProp: "id",
        },
      }}
    />
  );
};

const LmsServicingLoanSaleApprovalsEditForm: FC<
  LoanSaleApprovalsEditFormProps
> = ({ id }) => {
  const [{ data: adviceQuoteData, isLoading, error, fetchData }] = useFetchData(`purchasequotes/initial-data-for-edit/${id}`); 
  const [inputsCopy, setInputsCopy] = useState(undefined as any);
  const [dueToBcData, setDueToBcData] = useState(undefined as any);
  const [purchasersTotalPurchase, setPurchasersTotalPurchase] = useState(0 as number);
  const onPurchaseQuoteFieldChanged = (options, lastUpdated) => {
    if (lastUpdated === "receivedAmount") {
      const payoutResidualInput = options?.inputs?.fundTotalPurchase - Number(options?.inputs[lastUpdated]);
      options?.setInputs({ 
        ...options?.inputs, 
        "receivedAmount": options?.inputs[lastUpdated], 
        "payoutResidual": payoutResidualInput 
      });
    }

    setInputsCopy({ ...options?.inputs });
  };

  const handleApproveLoanSale = async (inputs, handleSubmit, useFormInstance) => {
    const formattedPayload = {
      ...inputs,
      "payoutReceived": inputs.receivedAmount,
      "purchaseQuoteLoanTransactionTempId": adviceQuoteData?.purchaseQuoteLoanTransactionTempId,
    };

    handleSubmit("purchasequotes/approve-purchase-advice-quote", { 
      method: "POST", 
      data: formattedPayload, 
      buttonText: useFormInstance.buttonText,
      onSuccessFunc: () => history.back()
    });
  };

  const handleDeclineLoanSale = async (inputs, handleSubmit, useFormInstance) => {
    const formattedPayload = {
      ...inputs,
      "payoutReceived": inputs.receivedAmount,
      "purchaseQuoteLoanTransactionTempId": adviceQuoteData?.purchaseQuoteLoanTransactionTempId
    };

    handleSubmit("purchasequotes/decline-purchase-advice-quote", { 
      method: "POST", 
      data: formattedPayload, 
      buttonText: useFormInstance.buttonText,
      onSuccessFunc: () => history.back()
    });
  };

  const myForm = purchaseAdviceQuoteViewFields(
    new BCFormController({ 
      ...adviceQuoteData, 
      "dueToBc": dueToBcData,
      "purchasersTotalPurchase": purchasersTotalPurchase,
      "payoutResidual": adviceQuoteData?.receivedAmount ? purchasersTotalPurchase - adviceQuoteData?.receivedAmount : purchasersTotalPurchase
    }, { values: { 
      loanId: adviceQuoteData?.loanId, 
      loanComments: adviceQuoteData?.loanComments,
      revenueRecognitionTempId: adviceQuoteData?.revenueRecognitionTempId,
      purchaseQuoteLoanTransactionTempId: adviceQuoteData?.purchaseQuoteLoanTransactionTempId,
      buildersCapitalPercentageId: adviceQuoteData?.buildersCapitalPercentageId,
      notionalBuildersCapitalPercentageId: adviceQuoteData?.notionalBuildersCapitalPercentageId,
      pdPeriod1: converToStartDateToEndDateString(adviceQuoteData?.pdStartDate1, adviceQuoteData?.pdEndDate1),
      pdPeriod2: converToStartDateToEndDateString(adviceQuoteData?.pdStartDate2, adviceQuoteData?.pdEndDate2),
    } }),
    false,
    true
  );

  useEffect(() => {
    if (adviceQuoteData) {
      if (adviceQuoteData?.saleTypeOption) {
        if (adviceQuoteData?.saleTypeOption === "Transfer fund to fund") {
          setPurchasersTotalPurchase(adviceQuoteData?.totalAmount);
        }
        else {
          setPurchasersTotalPurchase(adviceQuoteData?.totalPurchaseAmount);
        }
      }
      if (adviceQuoteData?.purchaseQuoteLoanTransactionTempId) {
        const getDueToBcValue = async () => {
          const { data: dueToBcValue } = await fetchData(`purchasequotes/due-to-bc/${adviceQuoteData?.purchaseQuoteLoanTransactionTempId}`, { fetchMethod: "GET" });
          setDueToBcData(dueToBcValue?.dueToBc);
        };
        
        getDueToBcValue();
      }
    }
  }, [adviceQuoteData]);

  return <LmsFormGenerator
    isLoading={isLoading}
    error={error}
    myForm={myForm}
    inputs={inputsCopy}
    actions={[{ text: "Approve", handleClick: handleApproveLoanSale }, { text: "Decline", handleClick: handleDeclineLoanSale }, "close"]}
    onFormChanged={onPurchaseQuoteFieldChanged}
  />;
};

const LmsServicingLoanSaleApprovals = ({ params = {}, history }) => {
  const { isMainPage, isEdit, isActionAllowed, id } = useParseRouteActions(
    params,
    ["edit"]
  );

  if (!isActionAllowed) {
    history.goBack();
  }

  return (
    <>
      {isMainPage && <LmsServicingLoanSaleApprovalsMainPage />}
      {isEdit && <LmsServicingLoanSaleApprovalsEditForm id={id} />}
    </>
  );
};

export default LmsServicingLoanSaleApprovals;
